import { defineComponent as _defineComponent } from 'vue'
import { computed, inject } from "vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import AffiliateMainInfoItem from "./AffiliateMainInfoItem.vue";
import { patchAffiliateSettings } from "@/api/user";
import { Affiliate } from "@/services/affiliates/Affiliate";
import {
    AFFILIATE_EDIT_KEY, AFFILIATE_INFO_EDIT_TYPE,
    AFFILIATE_INFO_TYPE,
    EDITABLE_FIELD,
} from "./types";
import { useCardEdit } from "./useCardEdit";

interface Props {
    affiliate: Affiliate;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PayoutsCard',
  props: {
    affiliate: null
  },
  emits: ["saved"],
  setup(__props: any, { emit }: { emit: ({ (e: 'saved'): void }), expose: any, slots: any, attrs: any }) {

const props = __props as {
    affiliate: Affiliate;
}



const ctx = inject(AFFILIATE_EDIT_KEY)!;

const OWNED_KEYS: EDITABLE_FIELD[] = [
    EDITABLE_FIELD.MASTER_EXTENSION,
    EDITABLE_FIELD.INVOICE_MASTER,
    EDITABLE_FIELD.BALANCE,
    EDITABLE_FIELD.HOLD_DAYS,
    EDITABLE_FIELD.HOLD_PAYMENTS,
    EDITABLE_FIELD.ADVANCE_PAYMENTS,
];

const masterExtensionItems = [
    { id: 'enabled', text: 'Расширенный' },
    { id: 'disabled', text: 'Обычный' },
];

const invoiceMasterItems = [
    { id: 'enabled', text: 'Общий' },
    { id: 'disabled', text: 'Раздельные' },
];

const holdPaymentItems = [
    { id: 'all', text: 'Для любых способов' },
    { id: 'cabinets', text: 'Только кабинеты' },
    { id: null, text: 'Нет' },
];

const translateMasterExtension = (value: string) => {
    const item = masterExtensionItems.find(i => i.id === value);
    return item?.text || value;
};

const translateInvoiceMaster = (value: string) => {
    const item = invoiceMasterItems.find(i => i.id === value);
    return item?.text || value;
};

const translateHoldPayment = (value: string) => {
    const item = holdPaymentItems.find(i => i.id === value);
    return item?.text || value;
};

const isEditableMasterExtension = computed(() => !!props.affiliate.has_slaves);

const isEditableInvoiceMaster = computed(() => {
    return isEditableMasterExtension.value && ctx.editableFields.value.master_extension === 'enabled';
});

const isMaster = computed(() => props.affiliate.master_id === null || props.affiliate.master_id === props.affiliate.id);

const isEditableBalance = computed(() => {
    return isMaster.value && !props.affiliate.balance?.enabled!;
});

const isEditableHold = computed(() => {
    const isBalanceEnabled = isEdit.value ?
        ctx.editableFields.value[EDITABLE_FIELD.BALANCE] === 'enabled' :
        props.affiliate.balance?.enabled!;
    return isMaster.value && isBalanceEnabled;
});

const updateSettings = async (key: EDITABLE_FIELD, value: any) => {
    await patchAffiliateSettings(props.affiliate.user_id!, { key, value });
};

const updateMasterExtension = async () => {
    const value = ctx.editableFields.value[EDITABLE_FIELD.MASTER_EXTENSION];
    if (value === 'disabled') {
        await updateSettings(EDITABLE_FIELD.INVOICE_MASTER, value);
    }
    await updateSettings(EDITABLE_FIELD.MASTER_EXTENSION, value);
};

const editMasterExtension = (_, value: string) => {
    ctx.editItem(EDITABLE_FIELD.MASTER_EXTENSION, value);
    if (value === 'disabled') {
        ctx.editItem(EDITABLE_FIELD.INVOICE_MASTER, 'disabled');
    }
};

const updateInvoiceMaster = async () => {
    await updateSettings(EDITABLE_FIELD.INVOICE_MASTER, ctx.editableFields.value[EDITABLE_FIELD.INVOICE_MASTER]);
};

const updateBalance = async () => {
    const value = ctx.editableFields.value[EDITABLE_FIELD.BALANCE];
    await updateSettings(EDITABLE_FIELD.BALANCE, value);
    if (value === 'enabled') {
        await updateSettings(EDITABLE_FIELD.ADVANCE_PAYMENTS, 'disabled');
    }
};

const editBalance = (_, value: string) => {
    ctx.editItem(EDITABLE_FIELD.BALANCE, value);
    if (value === 'enabled') {
        ctx.editItem(EDITABLE_FIELD.ADVANCE_PAYMENTS, 'disabled');
    }
};

const updateHoldDays = async () => {
    await updateSettings(EDITABLE_FIELD.HOLD_DAYS, ctx.editableFields.value[EDITABLE_FIELD.HOLD_DAYS]);
};

// === Card edit ===

const { isEdit, isLoading, isDisableEdit, editInfo, resetInfo } = useCardEdit({
    affiliateId: () => props.affiliate.id!,
    getItems: () => items.value,
    editableFields: () => ctx.editableFields.value,
    getChangedValues: () => ctx.getChangedValues.value,
    resetFields: ctx.resetFields,
    ownedKeys: OWNED_KEYS,
    onAfterSave: () => emit('saved'),
});

// === Items ===

const items = computed(() => ({
    master_extension: {
        type: AFFILIATE_INFO_TYPE.TEXT,
        name: 'Мастер-аккаунт: тип',
        value: ctx.editableFields.value[EDITABLE_FIELD.MASTER_EXTENSION],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
        selectItems: masterExtensionItems,
        isDisabled: !isEditableMasterExtension.value && isEdit.value,
        editMethod: editMasterExtension,
        updateMethod: updateMasterExtension,
        textValue: translateMasterExtension(ctx.editableFields.value[EDITABLE_FIELD.MASTER_EXTENSION]),
        hint: 'Расширенный мастер-аккаунт позволяет видеть мастеру данные его слейвов в своём аккаунте.',
    },
    invoice_master: {
        type: AFFILIATE_INFO_TYPE.TEXT,
        name: 'Мастер-аккаунт: счета',
        value: ctx.editableFields.value[EDITABLE_FIELD.INVOICE_MASTER],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
        selectItems: invoiceMasterItems,
        isDisabled: !isEditableInvoiceMaster.value && isEdit.value,
        editMethod: ctx.editItem,
        updateMethod: updateInvoiceMaster,
        textValue: translateInvoiceMaster(ctx.editableFields.value[EDITABLE_FIELD.INVOICE_MASTER]),
        hint: 'Если выбран общий счёт, то партнёру будет создаваться единый счёт с конверсиями всех слейвов. У слейвов отдельных счетов не будет.',
    },
    balance: {
        type: AFFILIATE_INFO_TYPE.SWITCH,
        name: 'Новый баланс',
        value: ctx.editableFields.value[EDITABLE_FIELD.BALANCE],
        isEditable: true,
        isDisabled: !isEditableBalance.value,
        editMethod: editBalance,
        updateMethod: updateBalance,
        hint: 'Переводит партнёра на новую систему выплат. ВНИМАНИЕ: после активации выключить эту настройку будет невозможно. ',
    },
    hold_days: {
        type: AFFILIATE_INFO_TYPE.TEXT,
        name: 'Индивид. холд',
        value: ctx.editableFields.value[EDITABLE_FIELD.HOLD_DAYS],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.NUMBER,
        max: 999,
        isDisabled: !isEditableHold.value,
        editMethod: ctx.editItem,
        updateMethod: updateHoldDays,
        hint: 'Определяет индивидуальный холд партнёра в днях.',
    },
    hold_payments: {
        type: AFFILIATE_INFO_TYPE.TEXT,
        name: 'Выплаты из холда',
        value: ctx.editableFields.value[EDITABLE_FIELD.HOLD_PAYMENTS],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
        selectItems: holdPaymentItems,
        textValue: translateHoldPayment(props.affiliate.balance?.hold_payments!),
        isDisabled: !isEditableHold.value,
        editMethod: ctx.editItem,
        hint: 'Определяет, куда партнёр может выводить средства их холда.',
    },
}));

return { __sfc: true,props, emit, ctx, OWNED_KEYS, masterExtensionItems, invoiceMasterItems, holdPaymentItems, translateMasterExtension, translateInvoiceMaster, translateHoldPayment, isEditableMasterExtension, isEditableInvoiceMaster, isMaster, isEditableBalance, isEditableHold, updateSettings, updateMasterExtension, editMasterExtension, updateInvoiceMaster, updateBalance, editBalance, updateHoldDays, isEdit, isLoading, isDisableEdit, editInfo, resetInfo, items, BaseEditableCard, BaseList, BaseListItem, AffiliateMainInfoItem }
}

})